import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button, Center, Text } from '@chakra-ui/react';

import { EllipsisLoading } from 'src/components/loadings';
import useUserStore, { useUserStatusSync } from 'src/hooks/stores/useUserStore';
import { useFeatureFlagsQuery } from 'src/hooks/queries/featureFlag';
import { useAuthModal } from 'src/modules/auth-modal-store';
import { Sections } from 'src/legacy/Components/auth/_constants';
import { useOtpReloadCheck } from 'src/components/OTP/hooks/useOtpReloadCheck';
import { useOtpLinkValidation } from 'src/components/OTP/hooks/useOtpLinkValidation';
import { useLandingAuthTransfer } from 'src/hooks/useLandingAuthTransfer';
import SentryProvider, { initSentry } from 'src/modules/sentry';
import { SentryBoundary } from 'src/modules/sentry-boundary';
import { lazyWithRetry } from 'src/modules/lazy-with-retry';
import { ChakraProvider } from 'src/modules/chakra';
import { ModalProvider } from 'src/modules/modal';
import { ReactQueryProvider } from 'src/modules/react-query';

import { Integrations } from './appConstants';

import './polyfills/fromEntries';

import './App.scss';
import 'src/legacy/Styles/Layout.scss';
import 'src/legacy/Styles/Reset.scss';
import 'src/legacy/Styles/Common.scss';

import ScrollToTop from 'src/components/ScrollToTop';

import { AppRoutes } from 'src/AppRoutes';
import UTMTracker from 'src/components/UTM/UTMTracker';
import {
	ensureDataLayer,
	scheduleAfterInteractionOrIdle,
	scheduleGtmInit,
	scheduleIdleTask,
} from 'src/modules/tracking';
import { EventPixelNavigationGuard } from 'src/modules/EventPixelNavigationGuard';

const AuthModal = lazyWithRetry(() =>
	import(
		/* webpackChunkName: "AuthModal" */
		'src/modules/auth-modal'
	).then(m => ({ default: m.AuthModal }))
);

const AppBootstrapRedirects = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: "AppBootstrapRedirects" */
			'src/AppBootstrapRedirects'
		)
);

// Força o tema light, sobrescrevendo um eventual 'dark' persistido pelo navegador
localStorage.setItem('chakra-ui-color-mode', 'light');

ensureDataLayer();
initSentry();

const isLandingPath = pathname =>
	pathname === '/solucoes' || pathname.startsWith('/solucoes/');

const isPublicEventRootPath = pathname =>
	pathname.startsWith('/evento/') && !pathname.includes('/checkout');

const isAuthPagePath = pathname =>
	pathname === '/login' || pathname === '/cadastro';

const isInputsPlaygroundPath = pathname =>
	process.env.NODE_ENV === 'development' && pathname === '/inputs';

const HOME_EDGE_TO_EDGE_CLASS = 'home-edge-to-edge';

const FullPageLoading = () => (
	<Center bg="white" position="absolute" top={0} left={0} right={0} h="100vh">
		<EllipsisLoading color="brand.500" />
	</Center>
);

const SharedPages = () => {
	const { pathname } = useLocation();
	const isAuthModalOpen = useAuthModal(state => state.isOpen);
	const isAuthPageRoute = isAuthPagePath(pathname);

	return (
		<>
			<AppRoutes />

			{isAuthModalOpen && !isAuthPageRoute && (
				<SentryBoundary flow="auth">
					<React.Suspense fallback={null}>
						<AuthModal />
					</React.Suspense>
				</SentryBoundary>
			)}
		</>
	);
};

const StandardPages = () => {
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const {
		defaultSection,
		isOpen: isAuthModalOpen,
		onCompleteSingup,
	} = useAuthModal();
	const isHomeRoute = pathname === '/';
	const isLandingRoute = isLandingPath(pathname);
	const isPublicEventRootRoute = isPublicEventRootPath(pathname);
	const hasGoogleOAuthParams =
		searchParams.has('state') && searchParams.has('code');
	const hasWirecardOAuthParams =
		searchParams.has('code') &&
		['/me/account', '/me/events'].includes(pathname);
	const hasPanelOAuthParams =
		searchParams.has('email') && searchParams.has('tkn');
	const shouldRunBootstrapRedirects =
		hasGoogleOAuthParams || hasWirecardOAuthParams || hasPanelOAuthParams;
	const shouldWaitForFeatureFlags =
		!isHomeRoute &&
		!isLandingRoute &&
		!isPublicEventRootRoute &&
		!shouldRunBootstrapRedirects;
	const featureFlagQuery = useFeatureFlagsQuery({
		enabled: shouldWaitForFeatureFlags,
	});
	const { userStatus } = useUserStore();
	const isCheckoutRoute = window.location.href.includes('checkout');
	const isAuthPageRoute = isAuthPagePath(pathname);

	useUserStatusSync();
	const isLandingAuthTransferReady = useLandingAuthTransfer();
	useOtpReloadCheck();
	useOtpLinkValidation({
		checkMeQueryStatus: true,
		validateAlways: true,
	});

	React.useEffect(() => {
		const userStatusValue = sessionStorage.getItem('userStatusValue');

		if (userStatusValue && !isCheckoutRoute) {
			sessionStorage.removeItem('userStatusValue');
		}

		if (
			userStatus === 'pending' &&
			!isCheckoutRoute &&
			!isAuthPageRoute &&
			!pathname.includes('/password-update') &&
			(!isAuthModalOpen || defaultSection !== Sections.COMPLETE_SINGUP)
		) {
			onCompleteSingup();
		}
	}, [
		defaultSection,
		isAuthModalOpen,
		isAuthPageRoute,
		isCheckoutRoute,
		onCompleteSingup,
		pathname,
		userStatus,
	]);

	if (!isLandingAuthTransferReady) {
		return <FullPageLoading />;
	}

	if (shouldRunBootstrapRedirects) {
		return (
			<>
				<FullPageLoading />
				<React.Suspense fallback={null}>
					<AppBootstrapRedirects />
				</React.Suspense>
			</>
		);
	}

	if (shouldWaitForFeatureFlags && featureFlagQuery.isLoading) {
		return <FullPageLoading />;
	}

	if (shouldWaitForFeatureFlags && featureFlagQuery.isError) {
		return (
			<Center color="gray.300" flexDirection="column" h="calc(100vh - 100px)">
				<Text fontSize="5xl" lineHeight="1" fontWeight="bold">
					!
				</Text>
				<Text fontSize="lg" maxW="md" textAlign="center" m={4}>
					Parece que algo deu errado
				</Text>
				<Button
					colorScheme="gray"
					rounded="full"
					onClick={featureFlagQuery.refetch}>
					Recarregar
				</Button>
			</Center>
		);
	}

	return <SharedPages />;
};

const LandingPages = () => {
	return <SharedPages />;
};

const Pages = () => {
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const hasBootstrapParams =
		searchParams.has('state') ||
		searchParams.has('code') ||
		searchParams.has('tkn') ||
		searchParams.has('uid') ||
		searchParams.has('otp');
	const isHomeRoute = pathname === '/';

	React.useLayoutEffect(() => {
		const { body } = document;

		if (isHomeRoute) {
			body.classList.add(HOME_EDGE_TO_EDGE_CLASS);
			body.style.removeProperty('height');

			return () => {
				body.classList.remove(HOME_EDGE_TO_EDGE_CLASS);
			};
		}

		const setBodyViewportHeight = () => {
			body.style.height = `${window.innerHeight}px`;
		};

		body.classList.remove(HOME_EDGE_TO_EDGE_CLASS);
		window.addEventListener('resize', setBodyViewportHeight);
		setBodyViewportHeight();

		return () => {
			window.removeEventListener('resize', setBodyViewportHeight);
		};
	}, [isHomeRoute]);

	if (isLandingPath(pathname) && !hasBootstrapParams) {
		return <LandingPages />;
	}

	if (isInputsPlaygroundPath(pathname)) {
		return <SharedPages />;
	}

	return <StandardPages />;
};

const App = () => {
	React.useEffect(() => {
		const isLandingInitialLoad = isLandingPath(window.location.pathname);

		const initializeAnalyticsIntegrations = () => {
			if (Integrations.GA_ID) {
				import(
					/* webpackChunkName: "ReactGA4" */
					'react-ga4'
				).then(({ default: ReactGA4 }) => {
					ReactGA4.initialize(Integrations.GA_ID);
				});
			}
		};

		const initializeDeferredMarketingIntegrations = () => {
			if (Integrations.GADS_ID) {
				import(
					/* webpackChunkName: "GoogleAds" */
					'src/legacy/Utils/GoogleAds'
				).then(({ default: googleAds }) => {
					googleAds(Integrations.GADS_ID);
				});
			}

			if (Integrations.INSPECTLET_ID) {
				import(
					/* webpackChunkName: "Inspectlet" */
					'src/legacy/Utils/Inspectlet'
				).then(({ Inspectlet }) => {
					Inspectlet();
				});
			}
		};

		if (isLandingInitialLoad) {
			scheduleIdleTask(initializeAnalyticsIntegrations, 1500);
			scheduleGtmInit(Integrations.GTM_ID, 1500);

			scheduleAfterInteractionOrIdle(() => {
				initializeDeferredMarketingIntegrations();
			}, 8000);

			return;
		}

		scheduleIdleTask(() => {
			initializeAnalyticsIntegrations();
			initializeDeferredMarketingIntegrations();
		});

		scheduleGtmInit(Integrations.GTM_ID);
	}, []);

	return (
		<ChakraProvider>
			<SentryProvider>
				<ReactQueryProvider>
					<ModalProvider>
						{/* <TawkMessengerReact
              propertyId={Integrations.TAWK_TO_ID}
              widgetId="default"
            /> */}
						<EventPixelNavigationGuard />
						<UTMTracker />
						<ScrollToTop />
						<SentryBoundary flow="app_shell">
							<Pages />
						</SentryBoundary>
					</ModalProvider>
				</ReactQueryProvider>
			</SentryProvider>
		</ChakraProvider>
	);
};

export default App;
